// src/Components/Containers/Books/BooksGrid.js
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {actions as booksActions, selectors as bookSelectors} from '../../../Ducks/books';
import {selectors as accountSelectors} from '../../../Ducks/account';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {fetchGet} from '../../../API/util';
import GridElement from '../../Common/GridElement';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Switch,
    Tooltip
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import BookFormModal from './BookFormModal';
import {toast} from 'react-toastify';
import {getBooks} from '../../../API/books';
import {useNavigate} from 'react-router';

const BOOK_COLUMNS = [
    {key: 'book_id', label: 'Book ID', disableFilters: false, sortable: true},
    {key: 'title', label: 'Title', disableFilters: false, sortable: true},
    {key: 'author', label: 'Author', disableFilters: false, sortable: true},
    {key: 'isbn', label: 'ISBN', disableFilters: false, sortable: true},
    {key: 'edition', label: 'Edition', disableFilters: true, sortable: true},
    {key: 'publisher', label: 'Publisher', disableFilters: true, sortable: true},
    {key: 'price', label: 'Price', disableFilters: true, sortable: true},
    {key: 'status', label: 'Status', disableFilters: true, sortable: false},
    {key: 'notes', label: 'Notes', disableFilters: true, sortable: false},
];

const makeHeader = (title) => {
    const parts = title.includes('.') ? title.split('.')[1].split('_') : title.split('_');
    return parts.join(' ');
};
const makeAccessor = (key) => (key.includes('.') ? key.split('.')[1] : key);

const parseRow = (raw) => {
    const item = {...raw};
    item.id = item.id ?? item.book_id ?? item.company_id;
    return item;
};

const BooksGrid = ({
                       createBook, updateBook, bulkImportBooks, archiveBook, restoreBook,
                       mutating, bulkImportResult, bulkImportResultError
                   }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const prevMutating = useRef(mutating);

    const [viewArchived, setViewArchived] = useState(false);
    const viewArchivedRef = useRef(viewArchived);
    const navigate = useNavigate();

    useEffect(() => {
        viewArchivedRef.current = viewArchived;
    }, [viewArchived]);

    // refresh grid when any mutation finishes
    useEffect(() => {
        if (prevMutating.current && !mutating) {
            setRefreshKey((k) => k + 1);
            if (importOpen && importing) {
                setImporting(false);
                setControlledPageIndex(0);
            }
            if (restoreMutating) {
                setRestoreMutating(false);
                if (confirmRestoreRow) {
                    toast.success(`Book "${confirmRestoreRow.title ?? confirmRestoreRow.book_id}" successfully restored`);
                    setConfirmRestoreRow(null);
                }
            }
        }
        prevMutating.current = mutating;
    }, [mutating]); // eslint-disable-line

    // when toggling archive view, reset to first page
    useEffect(() => {
        setControlledPageIndex(0);
        setRefreshKey((k) => k + 1);
    }, [viewArchived]);

    // Track restore mutations separately
    const [restoreMutating, setRestoreMutating] = useState(false);

    const [cols] = useState([
        ...BOOK_COLUMNS.map((item) => ({
            column: item.key,
            accessor: makeAccessor(item.key),
            minWidth: 150,
            Header: makeHeader(item.label),
            disableFilters: item.disableFilters,
            filter: 'fuzzyText',
        })),
        {
            column: '__actions',
            accessor: '__actions',
            Header: 'Actions',
            minWidth: 120,
            disableSortBy: true,
            filter: null,
            localOnly: true,
            disableFilters: true,
            Cell: ({row}) => renderActionsCell(row?.original),
        },
    ]);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [rowsCount, setRowsCount] = useState(0);
    const [filters, setFilters] = useState([]);
    const [controlledPageIndex, setControlledPageIndex] = useState(0);

    const [formOpen, setFormOpen] = useState(false);
    const [formRow, setFormRow] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmRow, setConfirmRow] = useState(null);

    // Restore confirmation state
    const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
    const [confirmRestoreRow, setConfirmRestoreRow] = useState(null);

    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState('');

    const fetchIdRef = useRef(0);

    const serverColumnsFrom = (columns) =>
        (columns || []).filter((c) => !c.localOnly && c.column !== '__actions').map((c) => c.column);

    const buildFilterQuery = (filtersArg, columnsArg) => {
        let query = '';
        filtersArg.forEach((item) => {
            const colMeta = columnsArg.find((c) => c.accessor === item.id);
            const source = colMeta?.column || item.id;
            let prefix = 'books';
            let field = item.id;
            if (source.includes('.')) {
                prefix = source.split('.')[0];
                field = source.split('.')[1];
            }
            query += `&contains[${prefix}.${field}]=${encodeURIComponent(item.value)}`;
        });
        return query;
    };

    const buildSortQuery = (sortBy, columnsArg) => {
        if (!sortBy?.length) {
            return {sortBy: 'created_at', order: 'desc'}; // default
        }

        const sb = sortBy[0]; // we only handle first sort for now
        const colMeta = columnsArg.find((c) => c.accessor === sb.id);
        const source = colMeta?.column || sb.id;

        let prefix = '';
        let field = sb.id;

        if (source.includes('.')) {
            prefix = source.split('.')[0] + '.';
            field = source.split('.')[1];
        }

        return {
            sortBy: `${prefix}${field}`,
            order: sb.desc ? 'desc' : 'asc',
        };
    };

    const normalizeResponse = (payload, pageSize) => {
        const d = payload?.data;
        if (!d) return {rows: [], total: 0, pageCount: 0};
        if (d?.main && d?.offset) {
            const rows = Array.isArray(d.main) ? d.main.map(parseRow) : [];
            const total = (d.offset?.rows_count ?? rows.length) ?? 0;
            const pageCount = (d.offset?.highest_offset ?? Math.ceil(total / (pageSize || 50)) ?? 0);
            return {rows, total, pageCount};
        }
        if (Array.isArray(d) && payload?.pagination) {
            const rows = d.map(parseRow);
            const total = (payload.pagination?.total ?? rows.length) ?? 0;
            const pageCount = (Math.ceil(total / (pageSize || 50)) ?? 0);
            return {rows, total, pageCount};
        }
        const rows = Array.isArray(d?.data) ? d.data.map(parseRow)
            : Array.isArray(d) ? d.map(parseRow) : [];
        const total = (d?.pagination?.total ?? rows.length) ?? 0;
        const pageCount = (Math.ceil(total / (pageSize || 50)) ?? 0);
        return {rows, total, pageCount};
    };

    const fetchData = useCallback(({pageSize, pageIndex, sortBy, filters: f, columns: c}) => {
        const fetchId = ++fetchIdRef.current;
        setLoading(true);

        const _columns = serverColumnsFrom(c);
        const filterQuery = buildFilterQuery(f, c);
        const sortQuery = buildSortQuery(sortBy, c);
        const rowOffset = pageIndex * pageSize;
        const status = viewArchivedRef.current ? 'archived' : 'active';

        getBooks({
            results: pageSize,
            search: filterQuery,
            columns: _columns,
            offset: rowOffset,
            sortBy: sortQuery.sortBy,
            order: sortQuery.order,
            status
        })
            .then((res) => {
                if (fetchId !== fetchIdRef.current) return;
                const {rows, total, pageCount} = normalizeResponse(res, pageSize);
                setData(rows);
                setRowsCount(total);
                setPageCount(pageCount);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    const resetFilters = useCallback(() => setFilters([]), [setFilters]);

    const renderActionsCell = (row) => (
        <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            <Tooltip title="Edit">
                <IconButton size="small" onClick={(e) => {
                    e.stopPropagation();
                    openEdit(row);
                }}>
                    <EditIcon fontSize="inherit"/>
                </IconButton>
            </Tooltip>

            {!viewArchivedRef.current ? (
                <Tooltip title="Archive">
                    <IconButton size="small" onClick={(e) => {
                        e.stopPropagation();
                        openArchive(row);
                    }}>
                        <ArchiveIcon fontSize="inherit"/>
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Restore">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            openRestore(row);
                        }}
                    >
                        <UnarchiveIcon fontSize="inherit" sx={{color: 'green'}}/>
                    </IconButton>
                </Tooltip>
            )}
        </div>
    );

    // create / edit
    const openCreate = () => {
        setFormOpen(true);
        setFormRow(null);
    };
    const openEdit = (row) => {
        setFormOpen(true);
        setFormRow(row);
    };
    const closeForm = () => {
        setFormOpen(false);
        setFormRow(null);
    };

    const saveForm = (idOrNull, payload) => {
        setIsSaving(true);
        if (idOrNull) {
            updateBook({id: idOrNull, obj: payload});
        } else {
            createBook(payload);
            setControlledPageIndex(0); // newest first
        }
        setIsSaving(false);
        closeForm();
    };

    // archive (confirm)
    const openArchive = async (row) => {
        const hasPendingOrders = row.pending_orders;
        const linkedProducts = row?.product_names?.map(item => item).join(', ');
        if (linkedProducts && linkedProducts.length > 0) {
            toast.error(
                `Cannot archive "${row.title ?? row.book_id}": This book is currently linked to ${linkedProducts} products and ${hasPendingOrders} pending book orders. Please unlink it or resolve pending orders first.`
            );
            return;
        }
        setConfirmOpen(true);
        setConfirmRow(row);
    };

    const closeArchive = () => {
        setConfirmOpen(false);
        setConfirmRow(null);
    };

    const confirmArchive = () => {
        if (!confirmRow) return;
        const id = confirmRow.id ?? confirmRow.book_id ?? confirmRow.company_id;
        archiveBook({id});
        toast.success(`Book "${confirmRow.title ?? confirmRow.book_id}" archived successfully.`);
        closeArchive();
    };

    // Restore (confirm)
    const openRestore = (row) => {
        setConfirmRestoreOpen(true);
        setConfirmRestoreRow(row);
    };

    const closeRestore = () => {
        setConfirmRestoreOpen(false);
        setConfirmRestoreRow(null);
    };

    const confirmRestore = () => {
        if (!confirmRestoreRow) return;
        const id = confirmRestoreRow.id ?? confirmRestoreRow.book_id ?? confirmRestoreRow.company_id;
        setRestoreMutating(true);
        restoreBook({id});
        closeRestore();
    };

    // import (multipart upload)
    const openImport = () => {
        setImportError('');
        setImportFile(null);
        setImportOpen(true);
    };
    const closeImport = () => {
        if (importing) return;
        setImportOpen(false);
        setImportError('');
        setImportFile(null);
        setImporting(false);
    };
    const onChooseFile = (e) => {
        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
        setImportFile(file);
        setImportError('');
    };
    const importBooks = async () => {
        if (!importFile) {
            setImportError('Please choose a .csv file');
            return;
        }
        if (!importFile.name.toLowerCase().endsWith('.csv')) {
            setImportError('Only .csv files are supported');
            return;
        }
        setImporting(true);
        await bulkImportBooks({file: importFile});
        closeImport();
    };

    const downloadSample = async () => {
        try {
            const blob = await fetchGet('/books/download/sample/csv', 0, {raw: true});

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'books_sample.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast.success('Sample CSV downloaded successfully.');
        } catch (_error) {
            toast.error('Failed to download sample CSV.');
        }
    };

    return (
        <>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16}}>
                <h2 className="page-title" style={{margin: 0}}>Books</h2>

                <FormControlLabel
                    control={
                        <Switch
                            checked={viewArchived}
                            onChange={(e) => setViewArchived(e.target.checked)}
                        />
                    }
                    label="Show Archived Books"
                />

                <div className="header-actions">
                    <Button variant="contained" color="primary" onClick={downloadSample} style={{margin: '1px 2px'}}>
                        Download Sample CSV
                    </Button>
                    <Button variant="contained" color="primary" onClick={openCreate} style={{margin: '1px 2px'}}>
                        Create Book
                    </Button>
                    <Button style={{margin: '1px 2px'}} variant="contained" color="primary" onClick={openImport}>
                        Import Books
                    </Button>
                    <Button style={{margin: '1px 2px'}} variant="contained" color="primary"
                            onClick={() => navigate('/books-imports-history')}>
                        Books Import History
                    </Button>
                </div>
            </div>

            <GridElement
                key={refreshKey}
                columns={cols}
                data={data}
                fetchData={fetchData}
                loading={loading}
                pageCount={pageCount}
                controlledPageIndex={controlledPageIndex}
                setControlledPageIndex={setControlledPageIndex}
                filters={filters}
                setFilters={setFilters}
                resetFilters={resetFilters}
                rowsCount={rowsCount}
                handleRowClick={() => {
                }}
                hideSelection
                initialSortBy="book_id"
            />

            {/* Create/Edit Book Modal */}
            <BookFormModal
                open={formOpen}
                onClose={closeForm}
                initialValues={formRow}
                onSave={saveForm}
                isSaving={isSaving}
            />

            {/* Archive confirm */}
            <Dialog open={confirmOpen} onClose={closeArchive} maxWidth="xs" fullWidth>
                <DialogTitle>Archive Book</DialogTitle>
                <DialogContent dividers>
                    Are you sure you want to archive
                    {confirmRow ? ` “${confirmRow.title ?? confirmRow.book_id}”` : ''}?
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeArchive}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmArchive}>Archive</Button>
                </DialogActions>
            </Dialog>

            {/* Restore confirm */}
            <Dialog open={confirmRestoreOpen} onClose={closeRestore} maxWidth="xs" fullWidth>
                <DialogTitle>Restore Book</DialogTitle>
                <DialogContent dividers>
                    Are you sure you want to restore
                    {confirmRestoreRow ? ` “${confirmRestoreRow.title ?? confirmRestoreRow.book_id}”? This book will become active again.` : ''}?
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRestore}>Cancel</Button>
                    <Button color="primary" variant="contained" onClick={confirmRestore}>Restore</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={importOpen} onClose={closeImport} maxWidth="sm" fullWidth>
                <DialogTitle>Import Books</DialogTitle>
                <DialogContent dividers>
                    <p style={{marginTop: 0}}>
                        Upload a CSV with headers that include at least:
                    </p>
                    <input
                        type="file"
                        name="file"
                        accept=".csv"
                        onChange={onChooseFile}
                        disabled={importing}
                        style={{margin: '8px 0'}}
                    />
                    {importError && <Alert severity="error" sx={{mt: 1}}>{importError}</Alert>}
                    {bulkImportResultError &&
                        <Alert severity="error" sx={{mt: 1}}>{bulkImportResultError?.message}</Alert>}
                    {importing && <LinearProgress sx={{mt: 2}}/>}
                    {!mutating && bulkImportResult && (
                        <div style={{marginTop: 12}}>
                            {Array.isArray(bulkImportResult.errors) && bulkImportResult.errors.length > 0 && (
                                <div style={{
                                    maxHeight: 180,
                                    overflow: 'auto',
                                    marginTop: 8,
                                    fontFamily: 'monospace',
                                    fontSize: 12
                                }}>
                                    {bulkImportResult.errors.map((e, i) => (
                                        <div key={i}>
                                            Row {e.row}: {e.reason}{e.isbn ? ` (ISBN: ${e.isbn})` : ''}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeImport} disabled={importing}>Close</Button>
                    <Button color="primary" variant="contained" onClick={importBooks}
                            disabled={importing || !importFile}>
                        {importing ? 'Uploading…' : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const mapStateToProps = (state) => ({
    user: accountSelectors.user(state),
    mutating: bookSelectors.isLoading(state),
    bulkImportResult: state.books?.data?.bulkImportResult,
    bulkImportResultError: state.books?.error,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            createBook: booksActions.createBook,
            updateBook: booksActions.updateBook,
            bulkImportBooks: booksActions.bulkImportBooks,
            archiveBook: booksActions.archiveBook,
            restoreBook: booksActions.restoreBook,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(BooksGrid);